import { all, fork, takeLatest, take, takeEvery } from 'redux-saga/effects';
import { rootAuthSaga, rootPreAuthSaga, rootAuthenticatedSaga as rootCoreAuthenticatedSaga } from 'flowhawk-core';

import storeDeviceToken from './DeviceSaga';
import {
  getApplicationDataSaga,
  clearChatroomNotificationsSaga,
  openChatroomSaga,
  loadChatMembersSaga,
  searchChatSaga,
  loadChatroomMessagesManager,
  handleAddedToChatroomSaga,
  handleRemovedFromChatroomSaga,
  addChatMessageWatcher,
  markMessagesReadSaga,
  acknowledgeMessageSaga,
  loadMessageStatusesSaga,
  saveChatSaga,
  startTypingSaga,
  stopTypingSaga,
  pinChatroomSaga,
  unpinChatroomSaga,
  loadChatFilesSaga,
  syncProfileInChatroomsSaga,
  redactMessageSaga,
  unredactMessageSaga,
  editMessageSaga,
  deleteFileMessageSaga,
  attemptReceiveNewMessageSaga,
  pinMessageSaga,
  unpinMessageSaga,
  loadPinnedMessagesSaga,
  updateRoomIconSaga,
} from './ChatSaga';
import {
  getContactsSaga,
  addUsersToChatroom,
  addSingleUserToChatroom,
  removeUserFromChatroom,
  deleteChatroom,
  inviteNewUser,
  toggleChatroomMute,
  muteCase,
  setNotificationSound,
  resendNotification,
  getChatroomInvitesSaga,
  deleteChatroomInviteSaga,
} from './ChatroomSaga';
import { loadOffersSaga, loadOrganizationsSaga, loadSurgeonsSaga, getOfferSaga, createOfferSaga } from './AskAlanSaga';
import {
  loadCasesSaga,
  getDonorSaga,
  updateDonorStatusSaga,
  updateAlertStatusSaga,
  createCaseServiceInfoRoomSaga,
  createDonorSaga,
  importDonorDataSaga,
  updateDonorSaga,
  loadDonorFilesSaga,
  uploadDonorFileSaga,
  deleteDonorFileSaga,
  loadOrgMembersSaga,
  updateTaskStatusSaga,
  getDonorTaskSaga,
  getDonorTaskDataSaga,
  updateTaskNoteSaga,
  updateDonorTaskDataSaga,
  updateTaskDueDateSaga,
  getDonorFollowerGroupsSaga,
  getFollowerGroupSaga,
  joinFollowerGroupSaga,
  createFollowerGroupSaga,
  createMultipleFollowerGroupsSaga,
  updateFollowerGroupSaga,
  updateFollowerGroupTasksSaga,
  deleteFollowerGroupSaga,
  updateFollowerGroupMembersSaga,
  updateFollowerGroupPermissionsSaga,
  removeUserFromFollowerGroupSaga,
  getPdfFieldsSaga,
  getCaseTimelineSaga,
  loadCasePreferencesSaga,
  inviteNewUserSaga,
  inviteUserSaga,
  patchUpdateCaseSaga,
  generateModularCasePDFSaga,
  setEditCaseTaskStatusSaga,
  pinCaseSaga,
  unpinCaseSaga,
  createCaseViewSaga,
  updateCaseViewSaga,
  deleteCaseViewSaga,
  deleteFollowerInviteSaga,
} from './DonorSaga';
import logEventSaga from './AnalyticsSaga';
import reportErrorSaga from './ErrorReportingSaga';
import {
  getFeaturePermissionsSaga,
  addPermissionSaga,
  addPermissionToAuthorizedEntitiesSaga,
  updatePermissionSaga,
} from './PermissionSaga';
import {
  loadAuthSaga,
  persistAuthSaga,
  deleteAuthSaga,
  refreshSuccessSaga,
  redirectToAdminPortalSaga,
  setCheckRegistrationStatusSaga,
  reconnectSocketOnProfileUpdateSaga,
} from './AuthSaga';
import { resetExpiredPasswordSaga, getPasswordPolicySaga } from './PasswordResetSaga';
import { getTeamsSaga, createTeamSaga, deleteTeamSaga, updateTeamSaga } from './TeamSaga';
import socketManager from './Socket';
import { getNotifiableEventGroupSaga } from './NotificationSaga';
import { getAllUsersSaga } from './NetworkSaga';
import { startRecipientUploadSaga } from './RecipientUploadSaga';
import type { SagaIterator } from 'redux-saga';
import {
  activityInstantWakeUpSaga,
  batchRetrieveActivityStatusesSaga,
  retrieveContactListActivityStatusesSaga,
  getActivityStatusHeartbeatSaga,
  hydrateRetrieveActivityStatusesSaga,
} from './ActivityStatusSaga';

function* startRootAuthSaga() {
  // Ensure any persisted JWT has been loaded into state in order to make requests
  // with credentials to avoid having to login again after page reload
  yield loadAuthSaga();

  yield all([
    takeLatest('Auth/SYNC_PROFILE_IN_CHATROOMS', syncProfileInChatroomsSaga),
    takeLatest('Auth/LOGIN_SUCCESS', persistAuthSaga),
    takeLatest('Auth/LOGIN_SUCCESS', setCheckRegistrationStatusSaga),
    takeLatest('Registration/CHECK_EMAIL_VERIFICATION_SUCCESS', setCheckRegistrationStatusSaga),
    takeLatest('Auth/REFRESH_SUCCESS', persistAuthSaga),
    takeLatest('Auth/STORE_SESSION_CASE_FILTER', persistAuthSaga),
    takeLatest('Auth/UPDATE_PROFILE_DATA', persistAuthSaga),
    takeLatest('Auth/UPDATE_PROFILE_DATA', reconnectSocketOnProfileUpdateSaga),
    takeLatest('Auth/REFRESH_SUCCESS', refreshSuccessSaga),
    takeLatest('Auth/LOGOUT_SUCCESS', deleteAuthSaga),
    takeLatest('Auth/TOKEN_EXPIRED', deleteAuthSaga),
    takeLatest('Auth/FORCE_RELOGIN', deleteAuthSaga),

    takeLatest('PasswordReset/RESET_EXPIRED_PASSWORD', resetExpiredPasswordSaga),
    takeLatest('PasswordReset/GET_PASSWORD_POLICY', getPasswordPolicySaga),

    takeLatest('Application/STORE_DEVICE_TOKEN', storeDeviceToken),
    takeLatest('Application/GET_APPLICATION_DATA', getApplicationDataSaga),
    takeEvery('Application/REPORT_ERROR', reportErrorSaga),

    takeLatest('ChatList/OPEN_CHATROOM', openChatroomSaga),
    takeLatest('ChatList/SEARCH', searchChatSaga),
    takeLatest('ChatList/LOAD_MEMBERS', loadChatMembersSaga),
    takeLatest('ChatEdit/SAVE', saveChatSaga),

    takeLatest('Chatroom/TOGGLE_MUTE', toggleChatroomMute),
    takeLatest('Chatroom/MUTE_CASE', muteCase),
    takeLatest('Chatroom/SET_NOTIFICATION_SOUND', setNotificationSound),
    takeLatest('Chatroom/UPDATE_ROOM_ICON', updateRoomIconSaga),
    takeLatest('ChatList/LOAD_FILES', loadChatFilesSaga),
    takeLatest('ChatList/CLEAR_NOTIFICATIONS', clearChatroomNotificationsSaga),
    takeLatest('ChatList/MARK_MESSAGES_READ', markMessagesReadSaga),
    takeLatest('ChatList/ACK_MESSAGE', acknowledgeMessageSaga),
    takeEvery('ChatList/START_TYPING', startTypingSaga),
    takeEvery('ChatList/STOP_TYPING', stopTypingSaga),
    takeLatest('ChatList/PIN_CHATROOM', pinChatroomSaga),
    takeLatest('ChatList/UNPIN_CHATROOM', unpinChatroomSaga),
    takeLatest('ChatList/REDACT_MESSAGE', redactMessageSaga),
    takeLatest('ChatList/DELETE_FILE_MESSAGE', deleteFileMessageSaga),
    takeLatest('ChatList/PIN_MESSAGE', pinMessageSaga),
    takeLatest('ChatList/UNPIN_MESSAGE', unpinMessageSaga),
    takeLatest('ChatList/LOAD_PINNED_MESSAGES', loadPinnedMessagesSaga),

    takeLatest('ChatList/UNREDACT_MESSAGE', unredactMessageSaga),

    takeLatest('ChatList/EDIT_MESSAGE', editMessageSaga),

    takeLatest('ChatList/LOAD_MSG_STATUSES', loadMessageStatusesSaga),
    takeEvery('ChatList/ATTEMPT_RECEIVE_NEW_MESSAGE', attemptReceiveNewMessageSaga),

    fork(loadChatroomMessagesManager),
    fork(socketManager),
    fork(addChatMessageWatcher),

    // User Activity Statuses
    fork(batchRetrieveActivityStatusesSaga),
    takeLatest('ActivityStatus/SUBSCRIBE_CONTACT_LIST_STATUSES', retrieveContactListActivityStatusesSaga),
    takeLatest('ActivityStatus/STATUS_HEARTBEAT', getActivityStatusHeartbeatSaga),
    takeLatest('ActivityStatus/ACTIVITY_WAKE_UP', activityInstantWakeUpSaga),
    takeLatest('ActivityStatus/HYDRATE_ACTIVITY_STATUSES', hydrateRetrieveActivityStatusesSaga),

    // Reload all chatrooms if we receive added/removed events
    takeEvery('ChatList/ADDED_TO_CHATROOM', handleAddedToChatroomSaga),
    takeLatest('ChatList/REMOVED_FROM_CHATROOM', handleRemovedFromChatroomSaga),

    takeLatest('Chatroom/GET_CONTACTS', getContactsSaga),
    takeEvery('Chatroom/ADD_USERS_TO_CHATROOM', addUsersToChatroom),
    takeEvery('Chatroom/ADD_SINGLE_USER_TO_CHATROOM', addSingleUserToChatroom),
    takeEvery('Chatroom/REMOVE_USER_FROM_CHATROOM', removeUserFromChatroom),
    takeEvery('Chatroom/DELETE_CHATROOM', deleteChatroom),

    takeLatest('Chatroom/RESEND_NOTIFICATION', resendNotification),

    takeEvery('Chatroom/INVITE_NEW_USER', inviteNewUser),

    takeLatest('ChatEdit/CREATE_OFFER', createOfferSaga),
    takeLatest('ChatEdit/GET_OFFERS', loadOffersSaga),
    takeLatest('ChatEdit/GET_ORGANIZATIONS', loadOrganizationsSaga),
    takeLatest('ChatEdit/GET_SURGEONS', loadSurgeonsSaga),
    takeLatest('ChatEdit/GET_OFFER', getOfferSaga),

    // Donor actions
    takeLatest('Donor/LOAD_CASES', loadCasesSaga),
    takeLatest('Donor/GET_DONOR', getDonorSaga),
    takeLatest('Donor/UPDATE_DONOR_STATUS', updateDonorStatusSaga),
    takeLatest('CaseAlerts/UPDATE_ALERT_STATUS', updateAlertStatusSaga),
    takeLatest('CaseServiceInfoRoom/CREATE_CASE_SERVICE_INFO_ROOM', createCaseServiceInfoRoomSaga),
    takeLatest('Donor/CREATE', createDonorSaga),
    takeLatest('Donor/IMPORT_DATA', importDonorDataSaga),
    takeLatest('Donor/UPDATE', updateDonorSaga),
    takeLatest('Donor/PATCH_UPDATE_CASE', patchUpdateCaseSaga),
    takeLatest('Donor/LOAD_FILES', loadDonorFilesSaga),
    takeLatest('Donor/UPLOAD_DONOR_FILE', uploadDonorFileSaga),
    takeLatest('Donor/DELETE_DONOR_FILE', deleteDonorFileSaga),
    takeLatest('Donor/GET_DONOR_TASK', getDonorTaskSaga),
    takeLatest('Donor/GET_DONOR_TASK_DATA', getDonorTaskDataSaga),
    takeEvery('Donor/UPDATE_TASK_STATUS', updateTaskStatusSaga),
    takeLatest('Donor/GET_ORG_MEMBERS', loadOrgMembersSaga),
    takeLatest('Donor/UPDATE_TASK_NOTE', updateTaskNoteSaga),
    takeLatest('Donor/UPDATE_TASK_DUE_DATE', updateTaskDueDateSaga),
    takeLatest('Donor/SET_EDIT_CASE_TASK_STATUS', setEditCaseTaskStatusSaga),
    takeLatest('Donor/PIN_CASE', pinCaseSaga),
    takeLatest('Donor/UNPIN_CASE', unpinCaseSaga),
    takeLatest('Form/UPDATE_DONOR_FORM', updateDonorTaskDataSaga),
    takeLatest('FollowerEdit/CREATE_FOLLOWER_GROUP', createFollowerGroupSaga),
    takeLatest('FollowerEdit/CREATE_MULTIPLE_FOLLOWER_GROUPS', createMultipleFollowerGroupsSaga),
    takeLatest('FollowerEdit/UPDATE_FOLLOWER_GROUP', updateFollowerGroupSaga),
    takeLatest('FollowerEdit/REMOVE_USER_FROM_FOLLOWERS', removeUserFromFollowerGroupSaga),
    takeEvery('FollowerEdit/INVITE_NEW_USER', inviteNewUserSaga),
    takeLatest('AddPeople/INVITE_NEW_USER', inviteUserSaga),
    takeLatest('Donor/UPDATE_FOLLOWER_GROUP_TASKS', updateFollowerGroupTasksSaga),
    takeLatest('Donor/DELETE_FOLLOWER_GROUP', deleteFollowerGroupSaga),
    takeLatest('Donor/UPDATE_FOLLOWER_GROUP_MEMBERS', updateFollowerGroupMembersSaga),
    takeLatest('Donor/UPDATE_FOLLOWER_GROUP_PERMISSIONS', updateFollowerGroupPermissionsSaga),
    takeLatest('Donor/GET_DONOR_FOLLOWER_GROUPS', getDonorFollowerGroupsSaga),
    takeEvery('Donor/GET_FOLLOWER_GROUP', getFollowerGroupSaga),
    takeLatest('FollowerEdit/JOIN_FOLLOWER_GROUP', joinFollowerGroupSaga),
    takeLatest('FollowerEdit/DELETE_FOLLOWER_INVITE', deleteFollowerInviteSaga),
    takeLatest('Chatroom/GET_CHATROOM_INVITES', getChatroomInvitesSaga),
    takeLatest('Chatroom/DELETE_CHATROOM_INVITE', deleteChatroomInviteSaga),
    takeLatest('Donor/LOAD_PDF_FIELDS', getPdfFieldsSaga),
    takeLatest('Donor/GET_CASE_TIMELINE', getCaseTimelineSaga),
    takeLatest('Donor/LOAD_CASE_PREFERENCES', loadCasePreferencesSaga),
    takeLatest('Donor/GENERATE_MODULAR_CASE_PDF', generateModularCasePDFSaga),
    takeLatest('Donor/CREATE_CASE_VIEW', createCaseViewSaga),
    takeLatest('Donor/UPDATE_CASE_VIEW', updateCaseViewSaga),
    takeLatest('Donor/DELETE_CASE_VIEW', deleteCaseViewSaga),

    takeEvery('Notification/GET_NOTIFIABLE_EVENT_GROUP', getNotifiableEventGroupSaga),

    // Recipient upload effects
    takeLatest('RecipientUpload/START_UPLOAD', startRecipientUploadSaga),

    takeLatest('Permission/GET_FEATURE_PERMISSIONS', getFeaturePermissionsSaga),
    takeEvery('Permission/ADD', addPermissionSaga),
    takeEvery('Permission/ADD_TO_AUTHORIZED_ENTITIES', addPermissionToAuthorizedEntitiesSaga),
    takeEvery('Permission/UPDATE', updatePermissionSaga),

    takeLatest('Team/GET_TEAMS', getTeamsSaga),
    takeLatest('Team/CREATE_TEAM', createTeamSaga),
    takeLatest('Team/DELETE_TEAM', deleteTeamSaga),
    takeLatest('Team/UPDATE', updateTeamSaga),

    takeEvery('Network/GET_ALL_USERS', getAllUsersSaga),

    takeEvery('Application/LOG_EVENT', logEventSaga),
    takeLatest('Application/REDIRECT_TO_ADMIN_PORTAL', redirectToAdminPortalSaga),
  ]);
}

export function* rootAuthenticatedSaga(): SagaIterator<void> {
  while (true) {
    const task = yield fork(startRootAuthSaga);

    // Previously we waited for the 'Auth/LOGOUT_SUCCESS', 'Auth/TOKEN_EXPIRED',
    // and 'Auth/FORCE_RELOGIN' actions. However, since we've changed the mechanism
    // for persisting credentials (a JWT), we listen for those events in the root saga
    // so that we can remove the credentials from storage and then we reload the page
    // to finish the logout process
    yield take(['App/RELOAD']);

    // and cancel the task
    if (task.isRunning()) {
      task.cancel();
    }

    // Now we're logged out (or even if we tried and failed), reload the dom to clear cached images
    window.location.reload();
  }
}

const allSagas = [rootAuthenticatedSaga, rootAuthSaga, rootPreAuthSaga, rootCoreAuthenticatedSaga];
export default allSagas;
